import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildRiskInput,
  UpdateRiskMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useChangeRequestConfirm } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '../_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '../invalidateChangeRequestFeeds';
import { unwrapApprovalGated } from '../unwrapApprovalGated';

type UpdateRiskTRPCInput = Omit<UpdateChildRiskInput, 'schedule'> &
  ScheduleTRPCInput;

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateRiskMutation => {
  return {
    updateChildRisk: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateRiskTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const changeRequestConfirm = useChangeRequestConfirm();
  const mutation = useMutation({
    ...trpc.frontend.risk.update.mutationOptions({
      onSuccess: async (result) => {
        // RSP-5789 — when the mutation was gated into a change request
        // (created or amended), surface it on the CR read surfaces
        // immediately instead of waiting for the panel's next poll.
        if (result.status !== 'applied') {
          await invalidateChangeRequestFeeds(queryClient, trpc);
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.scores.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskListOnlyOptimized.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.risk.riskListOnlyWithEntitiesOptimized.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskScoresByRiskId.queryKey(),
        });
      },
    }),
  });

  return {
    updateRisk: async (
      variables: UpdateRiskTRPCInput
    ): Promise<UpdateRiskMutation> => {
      const result = await withChangeRequestConfirm({
        input: variables,
        run: (v) => mutation.mutateAsync(v),
        confirm: changeRequestConfirm,
      });

      // A confirmed update resolves as `applied` (no chain fired) OR
      // `change-request-created` / `change-request-amended` (a CR was raised).
      // All three are success from the caller's perspective — mirror
      // useDeleteRiskTRPC and do NOT call expectApplied, which throws on the CR
      // branch and surfaces a false "Failed to save changes" toast. The entity
      // is unchanged on the CR branch, so echo the input Id.
      return mapTrpcResponseToGraphQL(
        unwrapApprovalGated(result, { Id: variables.Id })
      );
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
